// One menu to rule them all. A single hamburger button (top-left, works
// on mobile + desktop) that opens a Sheet with every surface in
// HackerLabs. Items that have an in-place sheet (Brain, Library, Tutor,
// Report a bug) dispatch their existing window events so users never
// leave the home prompt. Page items open in a wide right-side Sheet
// containing an iframe of the route — so users can multitask without
// losing the prompt they were composing.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Menu, X, Brain, Library as LibraryIcon, Bug, Trophy, Key,
  Sparkles, Bot, Users, Wallet,
  BadgeDollarSign, Settings, LogIn, User as UserIcon, ExternalLink,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

type Action =
  | { kind: "event"; event: string; label: string; Icon: any; group: string; hint?: string }
  | { kind: "popout"; to: string; label: string; Icon: any; group: string; hint?: string }
  | { kind: "link"; to: string; label: string; Icon: any; group: string; hint?: string };

const ITEMS: Action[] = [
  // Yours — in-place pops, never navigate
  { kind: "event",  event: "open-wallet",    label: "Wallet",   Icon: Wallet,        group: "Yours" },
  { kind: "event",  event: "open-memory",    label: "Brain",    Icon: Brain,         group: "Yours" },
  { kind: "event",  event: "open-library",   label: "Library",  Icon: LibraryIcon,   group: "Yours" },
  { kind: "event",  event: "open-byok-keys", label: "AI keys",  Icon: Key,           group: "Yours" },

  // Build — popout iframes so the home prompt stays mounted.
  { kind: "popout", to: "/workspace",   label: "Workspace", Icon: Sparkles, group: "Build" },
  { kind: "popout", to: "/agents",      label: "Agents",    Icon: Bot,      group: "Build" },

  // Community
  { kind: "popout", to: "/squads",      label: "Squads",    Icon: Users,           group: "Community" },
  { kind: "popout", to: "/leaderboard", label: "Leaders",   Icon: Trophy,          group: "Community" },

  // Account
  { kind: "popout", to: "/pricing",     label: "Pricing",   Icon: BadgeDollarSign, group: "Account" },
  { kind: "popout", to: "/settings",    label: "Settings",  Icon: Settings,        group: "Account" },
  { kind: "event",  event: "open-bug",  label: "Report a bug", Icon: Bug,          group: "Account" },
];

const GROUPS = ["Yours", "Build", "Community", "Account"] as const;

export default function UnifiedMenu() {
  const { user } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [popout, setPopout] = useState<{ to: string; label: string } | null>(null);

  // ESC closes the popout iframe even if focus is inside it.
  useEffect(() => {
    if (!popout) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setPopout(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [popout]);

  // Other surfaces (e.g. FunctionStrip) can request a popout by event.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const d = (e as CustomEvent<{ to: string; label: string }>).detail;
      if (d?.to) setPopout({ to: d.to, label: d.label ?? d.to });
    };
    window.addEventListener("open-popout", onOpen as EventListener);
    return () => window.removeEventListener("open-popout", onOpen as EventListener);
  }, []);

  const fire = (event: string) => {
    setMenuOpen(false);
    // Wait a tick so the menu sheet finishes closing before the next
    // sheet/popover opens — Radix doesn't love stacked overlays mid-animation.
    setTimeout(() => window.dispatchEvent(new Event(event)), 80);
  };

  const openPopout = (to: string, label: string) => {
    setMenuOpen(false);
    setTimeout(() => setPopout({ to, label }), 80);
  };

  return (
    <>
      {/* The single, always-visible menu trigger. Top-left, works on every
          breakpoint. */}
      <button
        type="button"
        onClick={() => setMenuOpen(true)}
        aria-label="Open menu"
        className="fixed top-3 left-3 z-40 h-10 w-10 grid place-items-center rounded-full border border-border bg-card/80 backdrop-blur hover:border-primary/50 hover:bg-card shadow-lg transition-colors"
      >
        <Menu className="w-4 h-4" />
      </button>

      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent side="left" className="w-[280px] sm:w-[320px] p-0 flex flex-col">
          <SheetHeader className="p-4 border-b border-border">
            <SheetTitle className="font-mono text-sm flex items-center gap-2">
              <Menu className="w-4 h-4 text-primary" /> Everything, one place
            </SheetTitle>
            <p className="text-[11px] text-muted-foreground font-mono">
              Tap to pop out — never lose your prompt.
            </p>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-3">
            {GROUPS.map((group) => {
              const items = ITEMS.filter((i) => i.group === group);
              if (!items.length) return null;
              return (
                <div key={group} className="mb-4">
                  <div className="px-2 pb-1 text-[9px] uppercase tracking-wider font-mono text-muted-foreground/70">
                    {group}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {items.map((item) => {
                      const Icon = item.Icon;
                      const inner = (
                        <>
                          <Icon className="w-4 h-4 shrink-0" />
                          <span className="text-xs font-mono truncate flex-1 text-left">{item.label}</span>
                          {item.kind === "popout" && <ExternalLink className="w-3 h-3 opacity-40" />}
                        </>
                      );
                      const className = cn(
                        "h-9 px-2 flex items-center gap-2 rounded-lg w-full",
                        "text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors",
                      );
                      if (item.kind === "event") {
                        return (
                          <button key={item.label} type="button" className={className} onClick={() => fire(item.event)}>
                            {inner}
                          </button>
                        );
                      }
                      if (item.kind === "popout") {
                        return (
                          <button key={item.label} type="button" className={className} onClick={() => openPopout(item.to, item.label)}>
                            {inner}
                          </button>
                        );
                      }
                      return (
                        <Link key={item.label} to={item.to} className={className} onClick={() => setMenuOpen(false)}>
                          {inner}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Auth row pinned to bottom */}
          <div className="border-t border-border p-3">
            {user ? (
              <Link
                to="/account"
                onClick={() => setMenuOpen(false)}
                className="h-10 px-3 flex items-center gap-2 rounded-lg border border-border hover:border-primary/50 text-xs font-mono"
              >
                <UserIcon className="w-4 h-4" />
                <span className="truncate">{user.email?.split("@")[0] ?? "Account"}</span>
              </Link>
            ) : (
              <Link
                to="/signup"
                onClick={() => setMenuOpen(false)}
                className="h-10 px-3 flex items-center gap-2 rounded-lg bg-primary text-primary-foreground text-xs font-mono font-semibold hover:opacity-90"
              >
                <LogIn className="w-4 h-4" /> Sign in
              </Link>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Page-in-a-drawer: load the route in an iframe so the home prompt
          stays mounted underneath. Same origin → no CORS/auth issues. */}
      <Sheet open={!!popout} onOpenChange={(v) => !v && setPopout(null)}>
        <SheetContent side="right" className="w-full sm:max-w-[720px] lg:max-w-[960px] p-0 flex flex-col">
          <SheetHeader className="p-3 border-b border-border flex-row items-center justify-between space-y-0">
            <SheetTitle className="font-mono text-xs flex items-center gap-2 truncate">
              <span className="text-muted-foreground">hackerlabs.ai</span>
              <span className="text-primary truncate">{popout?.to}</span>
            </SheetTitle>
            <div className="flex items-center gap-1">
              {popout && (
                <a
                  href={popout.to}
                  target="_blank"
                  rel="noreferrer"
                  className="h-7 px-2 inline-flex items-center gap-1 rounded-md border border-border text-[10px] font-mono text-muted-foreground hover:text-foreground"
                  title="Open in new tab"
                >
                  <ExternalLink className="w-3 h-3" /> Tab
                </a>
              )}
              <button
                type="button"
                onClick={() => setPopout(null)}
                className="h-7 w-7 grid place-items-center rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary/70"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </SheetHeader>
          {popout && (
            <iframe
              key={popout.to}
              src={popout.to}
              title={popout.label}
              className="flex-1 w-full bg-background"
            />
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}